/**
 * Video Call Container
 * Main full-screen video call interface
 */
import React, { useEffect, useRef, useState } from 'react';
import { useVideoCall, CallStatus } from '../../context/VideoCallContext';
import { useAuth } from '../../context/AuthContext';
import VideoCallControls from './VideoCallControls';
import MultiPartyVideoView from './MultiPartyVideoView';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Loader2, PhoneOff, Video, VideoOff, Mic, Square, X, Voicemail } from 'lucide-react';

const VideoCallContainer = () => {
  const { user } = useAuth();
  const {
    callState,
    mediaState,
    formatDuration,
    recordAndSendVoicemail,
    stopVoicemailRecording,
    skipVoicemail,
  } = useVideoCall();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const secondaryAudioRef = useRef(null);
  const secondaryVideoRef = useRef(null);
  const [isRecordingVm, setIsRecordingVm] = useState(false);

  const { status, duration, participants, isCaller, isMinimized, callMode, isOnHold, _voicemail, _pendingWarmTransfer, _isInConference } = callState;
  const { localStream, remoteStream, secondaryRemoteStream, isCameraOn } = mediaState;
  const isVoiceCall = callMode === 'voice';

  // Get the other participant
  const otherParticipant = participants.find(p => p.is_caller !== isCaller) || participants[isCaller ? 1 : 0];
  const selfParticipant = participants.find(p => p.is_caller === isCaller) || participants[isCaller ? 0 : 1];

  // Attach local stream to video element
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      console.log('[VideoCallContainer] Attaching local stream');
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Attach remote stream — to <video> for video calls, to <audio> for voice
  useEffect(() => {
    if (!remoteStream) return;
    if (isVoiceCall && remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = remoteStream;
    } else if (!isVoiceCall && remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream, isVoiceCall]);

  // While THIS party has the call on hold (we pressed Hold) OR while the other
  // party put us on hold, mute our incoming audio so we don't hear them. The
  // _heldByOther path covers Add-Participant / Warm-Transfer where A keeps
  // the WebRTC track enabled so C can still hear A — without this the held
  // party would also hear A briefing C.
  useEffect(() => {
    const muteIt = !!isOnHold || !!callState._heldByOther;
    if (remoteAudioRef.current) remoteAudioRef.current.muted = muteIt;
    if (remoteVideoRef.current) remoteVideoRef.current.muted = muteIt;
  }, [isOnHold, callState._heldByOther]);

  // Attach the secondary remote stream (C) to its audio/video element. When
  // we're pending merge (Add Participant flow), only the C-leg is audible —
  // B is held. When the conference is live, both are unmuted (audio is then
  // mixed on the wire by the Web Audio mixer in VideoCallContext).
  useEffect(() => {
    if (!secondaryRemoteStream) {
      if (secondaryAudioRef.current) secondaryAudioRef.current.srcObject = null;
      if (secondaryVideoRef.current) secondaryVideoRef.current.srcObject = null;
      return;
    }
    if (secondaryAudioRef.current) secondaryAudioRef.current.srcObject = secondaryRemoteStream;
    if (secondaryVideoRef.current) secondaryVideoRef.current.srcObject = secondaryRemoteStream;
  }, [secondaryRemoteStream]);

  // Don't render if idle or minimized
  if (status === CallStatus.IDLE || isMinimized) {
    return null;
  }

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleGradient = (role) => {
    switch (role) {
      case 'admin': return 'from-rose-500 to-pink-600';
      case 'supervisor': return 'from-blue-500 to-indigo-600';
      case 'lead': return 'from-emerald-500 to-teal-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case CallStatus.CALLING:
        return 'Calling...';
      case CallStatus.RINGING:
        return 'Ringing...';
      case CallStatus.CONNECTING:
        return 'Connecting...';
      case CallStatus.DECLINED:
        return 'Call Declined';
      case CallStatus.ENDED:
        return 'Call Ended';
      case CallStatus.FAILED:
        return 'Call Failed';
      case CallStatus.MISSED:
        return 'Missed Call';
      default:
        return null;
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[9998] bg-black flex flex-col"
      style={{
        // iter144 — Respect the iOS status bar / Dynamic Island when the
        // app runs as a PWA. Without this, the top-left "participant
        // info" badge and the top-right local-video PIP sit underneath
        // the iOS status-bar overlay and the user can't see who is on
        // the line / can't see their own preview. Also respects the
        // home-indicator on the bottom on the X-series and newer.
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      data-testid="video-call-container"
    >
      {/* iter147 — Multi-party video UI overhaul. Activated for VIDEO calls
          with 3+ participants (i.e., a 3-way conference or warm-transfer
          briefing). Voice-only and strict 2-party video calls keep the
          legacy single-feed layout below. */}
      {(() => {
        const isMultiParty = !isVoiceCall && (
          (participants?.length || 0) >= 3 ||
          !!_pendingWarmTransfer ||
          !!_isInConference ||
          !!secondaryRemoteStream
        );
        if (isMultiParty) {
          return (
            <MultiPartyVideoView callState={callState} mediaState={mediaState} user={user} />
          );
        }
        return null;
      })()}

      {/* Legacy 2-party / voice-only layout — keeps existing behavior */}
      {!(((participants?.length || 0) >= 3 || _pendingWarmTransfer || _isInConference || secondaryRemoteStream) && !isVoiceCall) && (
      <div className="flex-1 relative">
        {/* Remote video / avatar — voice-only calls fall back to avatar layout
            UNLESS the other side has just turned their camera on (their stream
            now contains a video track). */}
        {(() => {
          const hasRemoteVideo = !!(remoteStream && remoteStream.getVideoTracks().length > 0);
          const showVideo = (!isVoiceCall || hasRemoteVideo) && remoteStream;
          if (showVideo) {
            return (
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                muted={!!isOnHold || !!callState._heldByOther}
                className="w-full h-full object-cover"
                data-testid="remote-video"
              />
            );
          }
          return null;
        })() || (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
            <div className="text-center">
              <Avatar className="w-32 h-32 mx-auto mb-4 border-4 border-white/10">
                <AvatarImage src={otherParticipant?.profile_picture} />
                <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(otherParticipant?.role)} text-white text-4xl`}>
                  {getInitials(otherParticipant?.name)}
                </AvatarFallback>
              </Avatar>
              
              <h2 className="text-white text-2xl font-bold mb-1">
                {otherParticipant?.name || 'Unknown'}
              </h2>
              
              <p className="text-gray-400 text-sm capitalize mb-2">
                {otherParticipant?.role}
              </p>
              {isVoiceCall && (
                <p className="text-gray-500 text-xs uppercase tracking-widest mb-3">Voice call</p>
              )}
              {isOnHold && (
                <p className="text-amber-400 text-sm font-semibold mb-2">On hold</p>
              )}

              {/* Hidden audio element for the remote stream when voice-only.
                  We control mute via the JSX prop AND the useEffect below so
                  muted state is correct on first render and on every update. */}
              {isVoiceCall && (
                <audio
                  ref={remoteAudioRef}
                  autoPlay
                  playsInline
                  muted={!!isOnHold || !!callState._heldByOther}
                  data-testid="remote-audio"
                />
              )}

              {/* Status indicator */}
              {getStatusMessage() && (
                <div className="flex items-center justify-center gap-2 text-white">
                  {(status === CallStatus.CALLING || status === CallStatus.CONNECTING) && (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  )}
                  {(status === CallStatus.DECLINED || status === CallStatus.FAILED) && (
                    <PhoneOff className="w-5 h-5 text-red-500" />
                  )}
                  <span className="text-lg">{getStatusMessage()}</span>
                </div>
              )}
              
              {status === CallStatus.CONNECTED && (
                <div className="text-green-500 text-lg font-mono" data-testid="call-timer">
                  {formatDuration(duration)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Local video PIP — hide on voice-only calls AND when camera is off */}
        {!isVoiceCall && (
        <div className="absolute top-4 right-4 w-32 h-44 rounded-2xl overflow-hidden border-2 border-white/20 shadow-xl">
          {localStream && isCameraOn ? (
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
              data-testid="local-video"
            />
          ) : (
            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
              {!isCameraOn ? (
                <VideoOff className="w-8 h-8 text-gray-500" />
              ) : (
                <Avatar className="w-16 h-16">
                  <AvatarImage src={selfParticipant?.profile_picture} />
                  <AvatarFallback className="bg-gray-700 text-white">
                    {getInitials(selfParticipant?.name)}
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
          )}
        </div>
        )}

        {/* Participant info overlay */}
        {status === CallStatus.CONNECTED && (
          <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm rounded-xl px-4 py-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-white text-sm font-medium">
                {otherParticipant?.name}
                {_isInConference && secondaryRemoteStream ? ' + teammate' : ''}
              </span>
            </div>
          </div>
        )}

        {/* Conference / pending-merge indicator + hidden audio for the second
            leg so A can hear C while briefing (and both during conference). */}
        {(secondaryRemoteStream || _pendingWarmTransfer) && (
          <>
            <div className="absolute top-4 right-4 bg-amber-500/90 backdrop-blur-sm rounded-xl px-3 py-2 text-black text-xs font-semibold flex items-center gap-2"
                 data-testid="secondary-leg-indicator">
              <div className={`w-2 h-2 rounded-full ${_isInConference ? 'bg-emerald-700' : 'bg-orange-700'} animate-pulse`} />
              {_isInConference
                ? '3-way conference — all parties connected'
                : (_pendingWarmTransfer?.uiMode === 'add'
                    ? 'Briefing teammate — click Merge to bring them onto the line'
                    : 'Briefing teammate — click Complete to hand off')}
            </div>
            {/* Hidden audio for the secondary leg. We always render it so A
                hears C the moment the secondary peer connects. During hold,
                the primary <audio> is muted; the secondary plays. During
                conference, the Web Audio mixer in the context delivers mixed
                audio over the WebRTC senders — these elements stay live for
                local monitoring. */}
            <audio ref={secondaryAudioRef} autoPlay playsInline data-testid="secondary-remote-audio" />
            {!isVoiceCall && (
              <video
                ref={secondaryVideoRef}
                autoPlay
                playsInline
                className="absolute bottom-24 left-4 w-32 h-44 rounded-xl object-cover border-2 border-amber-500"
                data-testid="secondary-remote-video"
              />
            )}
          </>
        )}
      </div>
      )}

      {/* Controls bar */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <VideoCallControls />
      </div>

      {/* Voicemail overlay — shown after the ring timeout (30s, matches external Dial) */}
      {status === CallStatus.VOICEMAIL && (
        <div className="absolute inset-0 z-[10000] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6" data-testid="voicemail-overlay">
          <div className="max-w-md w-full bg-[#141414] border border-white/10 rounded-3xl p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-[#586D52]/20 flex items-center justify-center">
              <Voicemail className="w-8 h-8 text-[#586D52]" />
            </div>
            <h3 className="text-white text-xl font-bold mb-1" data-testid="voicemail-title">
              {_voicemail?.recipientName || 'Contact'} didn't answer
            </h3>
            <p className="text-gray-400 text-sm mb-5">After the tone, leave a voicemail or hang up.</p>
            {!isRecordingVm ? (
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={async () => {
                    setIsRecordingVm(true);
                    await recordAndSendVoicemail();
                  }}
                  className="bg-[#586D52] hover:bg-[#4a5d46]"
                  data-testid="voicemail-start-record"
                >
                  <Mic className="w-4 h-4 mr-2" />
                  Record voicemail
                </Button>
                <Button variant="outline" onClick={skipVoicemail} data-testid="voicemail-skip">
                  <X className="w-4 h-4 mr-2" />
                  Hang up
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-2 text-red-500 font-medium">
                  <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                  Recording…
                </div>
                <Button
                  variant="destructive"
                  onClick={() => { setIsRecordingVm(false); stopVoicemailRecording?.(); }}
                  data-testid="voicemail-stop-record"
                >
                  <Square className="w-4 h-4 mr-2" />
                  Stop & send
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCallContainer;
